import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { normalisePairingCode } from '../lib/pairing.js';
import type { PairingInfo } from '../lib/types.js';
import { Page } from '../app/AppShell.js';
import { QrScanner } from '../components/QrScanner.js';
import { useToast } from '../components/Toast.js';
import { Loading } from '../components/ui.js';

/**
 * Claim a screen that is showing a pairing QR (spec §2.3). A phone's camera
 * app opens `/claim?code=…` straight from the QR, so on iOS this page *is*
 * the pairing flow: confirm a name and the screen enrolls itself within a
 * few seconds. Without a code in the URL, type it or scan it here.
 */
export function ClaimDisplayScreen(): ReactNode {
  const [params, setParams] = useSearchParams();
  const code = normalisePairingCode(params.get('code') ?? '');

  return (
    <Page title="Add a screen">
      <div className="claim-page">
        {code ? (
          <ClaimForm key={code} code={code} onChangeCode={() => setParams({})} />
        ) : (
          <CodeEntry
            initial={params.get('code') ?? ''}
            autoScan={params.get('scan') === '1'}
            onCode={(next) => setParams({ code: next })}
          />
        )}
      </div>
    </Page>
  );
}

function CodeEntry({
  initial,
  autoScan,
  onCode,
}: {
  initial: string;
  /** Open the camera straight away (the box home's "Add screen"). */
  autoScan: boolean;
  onCode: (code: string) => void;
}): ReactNode {
  const [typed, setTyped] = useState(initial);
  const [scanning, setScanning] = useState(autoScan);
  const code = normalisePairingCode(typed);

  return (
    <div className="card card-pad stack">
      <p className="muted">
        Turn on the screen&rsquo;s player. It shows a QR code and an 8-character code. Scan the QR, or type the code.
      </p>
      <button className="btn primary" onClick={() => setScanning(true)}>
        Scan QR with camera
      </button>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (code) onCode(code);
        }}
      >
        <label className="field">
          Code on the screen
          <input
            className="claim-code-input"
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
              // A complete code needs no "Continue" tap.
              const complete = normalisePairingCode(e.target.value);
              if (complete) onCode(complete);
            }}
            placeholder="XXXX-XXXX"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
          />
        </label>
        {typed.trim() !== '' && !code ? <div className="form-error">That doesn&rsquo;t look like a screen code yet.</div> : null}
        <button className="btn" type="submit" disabled={!code}>
          Continue
        </button>
      </form>
      {scanning ? (
        <QrScanner
          onClose={() => setScanning(false)}
          onCode={(scanned) => {
            setScanning(false);
            onCode(scanned);
          }}
        />
      ) : null}
    </div>
  );
}

function ClaimForm({ code, onChangeCode }: { code: string; onChangeCode: () => void }): ReactNode {
  const navigate = useNavigate();
  const toast = useToast();
  const [info, setInfo] = useState<PairingInfo>();
  const [lookupError, setLookupError] = useState<string>();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    api
      .pairingInfo(code)
      .then((found) => {
        if (cancelled) return;
        setInfo(found);
        setName((current) => current || found.device.model || '');
      })
      .catch((err: unknown) => {
        if (!cancelled) setLookupError(describe(err));
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  async function claim(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      const display = await api.claimPairing(code, name.trim());
      toast.push(`“${display.name}” added. It will start playing in a few seconds.`, 'success');
      navigate('/displays', { replace: true });
    } catch (err) {
      setError(describe(err));
      setBusy(false);
    }
  }

  if (lookupError) {
    return (
      <div className="card card-pad stack">
        <div className="form-error">{lookupError}</div>
        <button className="btn" onClick={onChangeCode}>
          Try another code
        </button>
      </div>
    );
  }

  if (!info) return <Loading what="screen" />;

  const facts = [
    info.device.model,
    info.device.shell ? `${info.device.shell} player` : undefined,
    info.device.screenWidth && info.device.screenHeight ? `${info.device.screenWidth}×${info.device.screenHeight}` : undefined,
  ].filter(Boolean);

  return (
    <form
      className="card card-pad stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() !== '') void claim();
      }}
    >
      <div>
        <div className="claim-code-input">{code}</div>
        {facts.length > 0 ? <div className="muted">{facts.join(' · ')}</div> : null}
      </div>
      <label className="field">
        Name
        <span className="hint">Where the screen lives — “Lobby north wall”, “Cafe menu”.</span>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>
      {error ? <div className="form-error">{error}</div> : null}
      <div className="row between">
        <button className="btn" type="button" onClick={onChangeCode}>
          Different code
        </button>
        <button className="btn primary" type="submit" disabled={busy || name.trim() === ''}>
          {busy ? 'Adding…' : 'Add screen'}
        </button>
      </div>
    </form>
  );
}

function describe(err: unknown): string {
  if (err instanceof ApiError && err.status === 410) {
    return 'That code has expired or was already used. Check the code on the screen — it refreshes every 15 minutes.';
  }
  if (err instanceof ApiError) return err.fieldMessage() ?? err.message;
  return err instanceof Error ? err.message : String(err);
}
